import { FunctionComponent, SVGProps } from 'react';
import { ReactComponent as SystemMessageOperationalIcon } from 'assets/icons/system-message-operational.svg';
import { ReactComponent as SystemMessageAlertIcon } from 'assets/icons/system-message-alert.svg';
import { ReactComponent as SystemMessageFeatureIcon } from 'assets/icons/system-message-feature.svg';
import { ReactComponent as SystemMessageHolidayIcon } from 'assets/icons/system-message-holiday.svg';
import { ReactComponent as SystemMessagePolicyIcon } from 'assets/icons/system-message-policy.svg';
import { ReactComponent as SystemMessageGeneralIcon } from 'assets/icons/system-message-general.svg';

export const SystemMessageIconType = {
	OPERATIONAL: 'operational',
	ALERT: 'alert',
	FEATURE: 'feature',
	HOLIDAY: 'holiday',
	POLICY: 'policy',
	GENERAL: 'general',
} as const;

export type SystemMessageIconType = (typeof SystemMessageIconType)[keyof typeof SystemMessageIconType];

export type SystemMessageIconComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

export const SYSTEM_MESSAGE_ICON_MAP: Record<SystemMessageIconType, SystemMessageIconComponent> = {
	[SystemMessageIconType.OPERATIONAL]: SystemMessageOperationalIcon,
	[SystemMessageIconType.ALERT]: SystemMessageAlertIcon,
	[SystemMessageIconType.FEATURE]: SystemMessageFeatureIcon,
	[SystemMessageIconType.HOLIDAY]: SystemMessageHolidayIcon,
	[SystemMessageIconType.POLICY]: SystemMessagePolicyIcon,
	[SystemMessageIconType.GENERAL]: SystemMessageGeneralIcon,
};

const SYSTEM_MESSAGE_ICON_TYPES = new Set<string>(Object.values(SystemMessageIconType));

export const resolveSystemMessageIcon = (iconType: string) => {
	const resolvedType = SYSTEM_MESSAGE_ICON_TYPES.has(iconType)
		? (iconType as SystemMessageIconType)
		: SystemMessageIconType.GENERAL;

	return {
		Icon: SYSTEM_MESSAGE_ICON_MAP[resolvedType],
		labelKey: `portal.systemMessage.icon.${resolvedType}`,
	};
};

export const formatSystemMessageCounter = (template: string, index: number, total: number) => {
	/** U+2066 — bidi "left-to-right isolate": numbers read LTR inside Hebrew/RTL layout. */
	const UNICODE_LEFT_TO_RIGHT_ISOLATE = '\u2066';
	/** U+2069 — ends the isolate started above. */
	const UNICODE_POP_DIRECTIONAL_ISOLATE = '\u2069';

	const wrapLtr = (value: string | number) =>
		`${UNICODE_LEFT_TO_RIGHT_ISOLATE}${value}${UNICODE_POP_DIRECTIONAL_ISOLATE}`;

	return template.replace('{{index}}', wrapLtr(index + 1)).replace('{{total}}', wrapLtr(total));
};
